import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { QuestionnaireWizard } from '@/components/auth/QuestionnaireWizard'
import { ProfileTypeStep } from '@/components/auth/ProfileTypeStep'
import { BasicInfoStep } from '@/components/auth/BasicInfoStep'
import { PasswordStep } from '@/components/auth/PasswordStep'
import { SpecificInfoStep } from '@/components/auth/SpecificInfoStep'
import { PreferencesStep } from '@/components/auth/PreferencesStep'
import { ReviewStep } from '@/components/auth/ReviewStep'
import { useAuthStore } from '@/stores/authStore'
import { profileService } from '@/services/profileService'
import { authService } from '@/services/authService'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

interface FormData {
  // Step 1
  profileType: string

  // Step 2
  name: string
  email: string
  cpf: string
  password: string
  confirmPassword: string
  username: string
  phone: string
  city: string
  state: string
  profession: string
  howKnew: string
  aiFamiliarity: string
  termsAccepted: boolean

  // Step 3 (específicos por perfil)
  propertySizeHa?: string
  biome?: string
  soilType?: string
  propertyCity?: string
  propertyState?: string
  productionSystem?: string
  gaasAssociated?: string
  activities?: string[]
  practices?: string[]
  challenges?: string[]
  mainObjective?: string
  professionalFormation?: string
  workArea?: string
  workRegion?: string
  workType?: string
  regenerativeExperience?: string
  experienceYears?: string
  howUseRegenerai?: string
  institution?: string
  workField?: string
  mainInterest?: string

  // Step 4
  aiUsage?: string
  preferredTopics?: string[]
  dataSharingConsent?: string
  observations?: string
}

export default function RegisterQuestionnaire() {
  const navigate = useNavigate()
  const { register } = useAuthStore()
  const [registrationBlocked, setRegistrationBlocked] = useState(false)

  // Check if registration is enabled
  useEffect(() => {
    axios.get(`${API_URL}/auth/public/settings`)
      .then(res => {
        if (!res.data.registration_enabled) {
          setRegistrationBlocked(true)
        }
      })
      .catch(() => { }) // fail-open
  }, [])

  if (registrationBlocked) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full card p-8 text-center">
          <h2 className="text-xl font-semibold text-brand-text mb-3">Cadastro indisponivel</h2>
          <p className="text-brand-text-secondary mb-6">
            O cadastro de novos usuarios esta temporariamente desabilitado.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary px-6 py-2"
          >
            Voltar ao login
          </button>
        </div>
      </div>
    )
  }

  // Carregar progresso do LocalStorage
  //
  // SEGURANCA: Como dados sensiveis (nome, email, CPF, username, senha, aceite
  // de termos) NAO sao salvos no localStorage, restaurar o usuario diretamente
  // em um step posterior pode levar a um cadastro com campos vazios.
  // Por isso, limitamos o step restaurado a no maximo 1 (Cadastro Basico).
  const loadProgress = () => {
    try {
      const saved = localStorage.getItem('registerProgress')
      if (saved) {
        const progress = JSON.parse(saved)
        const savedStep = Number(progress.currentStep) || 0
        const restoredStep = Math.min(savedStep, 1) // nunca alem de Cadastro Basico
        return {
          currentStep: restoredStep,
          formData: {
            profileType: progress.profileType || '',
            name: '',
            email: '',
            cpf: '',
            username: '',
            phone: '',
            city: '',
            state: '',
            profession: '',
            howKnew: '',
            aiFamiliarity: '',
            termsAccepted: false,
            ...progress.safeData // Dados seguros salvos
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar progresso:', error)
    }
    return { currentStep: 0, formData: {} }
  }

  const initialData = loadProgress()

  const [currentStep, setCurrentStep] = useState(initialData.currentStep)
  const [formData, setFormData] = useState<FormData>({
    profileType: 'producer', // Definido como padrão
    name: '',
    email: '',
    cpf: '',
    password: '',
    confirmPassword: '',
    username: '',
    phone: '',
    city: '',
    state: '',
    profession: '',
    howKnew: '',
    aiFamiliarity: '',
    termsAccepted: false,
    ...initialData.formData
  })

  const [stepValidation, setStepValidation] = useState<boolean[]>([
    true, // profileType - válido por padrão com 'producer'
    false, // basicInfo
    false, // password (novo)
    true,  // specificInfo (opcional)
    true,  // preferences (opcional)
    true   // review (sempre válido)
  ])

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Validação de step
  const updateStepValidation = (stepIndex: number, isValid: boolean) => {
    setStepValidation(prev => {
      const newValidation = [...prev]
      newValidation[stepIndex] = isValid
      return newValidation
    })
  }

  // Salvar progresso no LocalStorage (apenas dados seguros)
  const saveProgress = (step: number, data: Partial<FormData>) => {
    try {
      const safeData = {
        currentStep: step,
        profileType: data.profileType,
        // Adicionar outros campos seguros aqui
        city: data.city,
        state: data.state,
        profession: data.profession,
        howKnew: data.howKnew,
        aiFamiliarity: data.aiFamiliarity,
        // NÃO salvar: name, email, cpf, username, phone, termsAccepted
      }
      localStorage.setItem('registerProgress', JSON.stringify(safeData))
    } catch (error) {
      console.error('Erro ao salvar progresso:', error)
    }
  }

  // Atualizar dados do formulário
  const handleFormDataChange = (data: Partial<FormData>) => {
    const newData = { ...formData, ...data }
    setFormData(newData)

    // Salvar progresso automaticamente
    saveProgress(currentStep, newData)
  }

  // Verificar se step atual é válido
  const isCurrentStepValid = stepValidation[currentStep]

  // Submeter formulario
  const handleSubmit = async () => {
    setIsSubmitting(true)
    setErrors({})

    try {
      // ----------------------------------------------------------------------
      // 0. PRE-FLIGHT: garantir que os steps obrigatorios estao validos.
      //
      // Como o ReviewStep dispara onConfirm sem checar steps anteriores, e o
      // localStorage pode restaurar o usuario com campos vazios, validamos
      // explicitamente aqui antes de chamar a API. Se algo estiver faltando,
      // voltamos para o primeiro step nao-valido com mensagem clara.
      // ----------------------------------------------------------------------
      if (!stepValidation[1]) {
        setErrors({ general: 'Complete os dados basicos antes de finalizar.' })
        setCurrentStep(1)
        setIsSubmitting(false)
        return
      }
      if (!stepValidation[2]) {
        setErrors({ general: 'Complete a senha antes de finalizar.' })
        setCurrentStep(2)
        setIsSubmitting(false)
        return
      }
      if (!formData.email || !formData.password || !formData.name) {
        setErrors({ general: 'Dados basicos incompletos. Refaca o cadastro.' })
        setCurrentStep(1)
        setIsSubmitting(false)
        return
      }

      // ----------------------------------------------------------------------
      // 1. VALIDAR (pre-check de duplicidade) email/CPF/username.
      // ----------------------------------------------------------------------
      const validationResult = await authService.validateRegistration({
        email: formData.email,
        cpf: formData.cpf || undefined,
        username: formData.username || undefined,
      })

      if (!validationResult.success && validationResult.details) {
        const validationErrors: Record<string, string> = {}
        if (validationResult.details.email) validationErrors.email = validationResult.details.email
        if (validationResult.details.cpf) validationErrors.cpf = validationResult.details.cpf
        if (validationResult.details.username) validationErrors.username = validationResult.details.username

        setErrors(validationErrors)
        setCurrentStep(1) // Voltar para o step com os campos
        setIsSubmitting(false)
        return
      }

      // ----------------------------------------------------------------------
      // 2. CRIAR usuario com TODOS os campos essenciais em uma unica transacao
      //    (email, senha, nome, CPF, username, telefone, localizacao, perfil
      //    declarado e aceite legal LGPD).
      // ----------------------------------------------------------------------
      await register({
        // Credenciais
        name: formData.name,
        email: formData.email,
        password: formData.password,

        // Identificacao extra
        username: formData.username || undefined,
        cpf: formData.cpf
          ? formData.cpf.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') || undefined
          : undefined,
        phone: formData.phone || undefined,

        // Localizacao e perfil declarado
        city: formData.city || undefined,
        state: formData.state || undefined,
        profession: formData.profession || undefined,
        profileType: formData.profileType || undefined,
        howKnew: formData.howKnew || undefined,
        aiFamiliarity: formData.aiFamiliarity || undefined,

        // Aceite legal LGPD (IP gravado pelo backend via request.ip)
        termsAccepted: true,
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: '1.0',
        termsAcceptedUserAgent: navigator.userAgent,
        privacyAcceptedAt: new Date().toISOString(),
        privacyVersion: '1.0',
      })

      // ----------------------------------------------------------------------
      // 3. ATUALIZAR perfil com dados OPCIONAIS especificos do questionario
      //    (perfil de produtor/profissional/academico/preferencias).
      //
      //    Diferente da versao anterior, dados essenciais ja foram persistidos
      //    no /auth/register. Se essa etapa falhar, avisamos o usuario, mas
      //    nao bloqueamos o acesso (a conta ja esta criada).
      // ----------------------------------------------------------------------
      const optionalProfileData = {
        bio: formData.observations || '',

        // Dados de propriedade (produtor)
        propertySizeHa: formData.propertySizeHa ? parseFloat(formData.propertySizeHa) : undefined,
        biome: formData.biome,
        soilType: formData.soilType,
        propertyCity: formData.propertyCity,
        propertyState: formData.propertyState,
        productionSystem: formData.productionSystem,
        gaasAssociated: formData.gaasAssociated,
        activities: formData.activities,
        practices: formData.practices,
        challenges: formData.challenges,
        mainObjective: formData.mainObjective,

        // Dados profissionais (tecnico/consultor)
        professionalFormation: formData.professionalFormation,
        workArea: formData.workArea,
        workRegion: formData.workRegion,
        workType: formData.workType,
        regenerativeExperience: formData.regenerativeExperience,
        experienceYears: formData.experienceYears,

        // Dados academicos (pesquisador/estudante)
        institution: formData.institution,
        workField: formData.workField,
        mainInterest: formData.mainInterest,

        // Preferencias
        preferredTopics: formData.preferredTopics,

        // Consentimento de dados
        dataSharingConsent: formData.dataSharingConsent,
      }

      let optionalProfileFailed = false
      try {
        await profileService.updateProfile(optionalProfileData)
      } catch (profileError) {
        // Conta ja criada com dados essenciais. Apenas dados opcionais do
        // questionario nao foram salvos. Avisamos o usuario para complementar
        // depois nas configuracoes.
        console.error('Falha ao salvar dados opcionais do questionario:', profileError)
        optionalProfileFailed = true
      }

      // Limpar progresso do LocalStorage
      localStorage.removeItem('registerProgress')

      if (optionalProfileFailed) {
        // Salvar flag para mostrar aviso na pagina de destino
        try {
          sessionStorage.setItem(
            'profile_complete_pending',
            'Conta criada! Algumas preferencias do questionario nao foram salvas. Voce pode completa-las nas configuracoes do perfil.'
          )
        } catch {
          // ignore
        }
      }

      // Redirecionar para chat (pagina principal apos login)
      navigate('/chat')

    } catch (error: any) {
      console.error('Erro no cadastro:', error)

      // Conflito de duplicidade: backend retorna { error, field, details }
      const status = error.response?.status
      const responseData = error.response?.data

      if (status === 409 && responseData?.field) {
        const field: string = responseData.field
        const message: string = responseData.error || responseData.details?.[field]
        setErrors({ [field]: message })
        setCurrentStep(1)
        setIsSubmitting(false)
        return
      }

      // Erro de validacao Zod do backend (formato/forca de senha/CPF invalido)
      if (status === 400 && Array.isArray(responseData?.details)) {
        const fieldErrors: Record<string, string> = {}
        for (const item of responseData.details) {
          if (item?.field && item?.message) {
            // Mapear campos do schema para o step correspondente
            fieldErrors[item.field] = item.message
          }
        }
        setErrors(fieldErrors)
        // Se houve erro de senha, voltar para step 2; senao step 1
        if (fieldErrors.password) {
          setCurrentStep(2)
        } else {
          setCurrentStep(1)
        }
        setIsSubmitting(false)
        return
      }

      // Mensagens textuais (compat com erros lancados pelo authStore)
      if (responseData?.error) {
        const errorMessage: string = responseData.error
        if (errorMessage.includes('Email')) {
          setErrors({ email: errorMessage })
          setCurrentStep(1)
        } else if (errorMessage.includes('CPF')) {
          setErrors({ cpf: errorMessage })
          setCurrentStep(1)
        } else if (errorMessage.includes('Username')) {
          setErrors({ username: errorMessage })
          setCurrentStep(1)
        } else {
          setErrors({ general: errorMessage })
        }
      } else {
        setErrors({ general: 'Erro ao criar cadastro. Tente novamente.' })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Navegação entre steps
  const handleStepChange = (step: number) => {
    // Só permitir navegar para trás ou para frente se o step atual for válido
    if (step < currentStep || isCurrentStepValid) {
      setCurrentStep(step)
      // Salvar progresso do step atual
      saveProgress(step, formData)
    }
  }

  // Configurar os steps
  const steps = [
    {
      id: 'profile',
      title: 'Seu Perfil',
      description: 'Para que o RegenerAI entregue respostas mais adequadas à sua realidade, selecione o perfil que melhor descreve você:',
      component: (
        <ProfileTypeStep
          value={formData.profileType}
          onChange={(value) => handleFormDataChange({ profileType: value })}
          onValidationChange={(isValid) => updateStepValidation(0, isValid)}
        />
      ),
      isValid: stepValidation[0]
    },
    {
      id: 'basic',
      title: 'Cadastro Básico',
      description: 'Informações obrigatórias para criar seu cadastro',
      component: (
        <BasicInfoStep
          data={formData}
          onChange={handleFormDataChange}
          onValidationChange={(isValid) => updateStepValidation(1, isValid)}
          errors={errors}
        />
      ),
      isValid: stepValidation[1]
    },
    {
      id: 'password',
      title: 'Senha de Acesso',
      description: 'Use uma senha forte para proteger sua conta',
      component: (
        <PasswordStep
          data={{
            password: formData.password,
            confirmPassword: formData.confirmPassword
          }}
          onChange={handleFormDataChange}
          onValidationChange={(isValid) => updateStepValidation(2, isValid)}
        />
      ),
      isValid: stepValidation[2]
    },
    {
      id: 'specific',
      title: 'Informações Específicas',
      description: '',
      component: (
        <SpecificInfoStep
          profileType={formData.profileType}
          data={formData}
          onChange={handleFormDataChange}
          onValidationChange={(isValid) => updateStepValidation(3, isValid)}
        />
      ),
      isValid: stepValidation[3]
    },
    {
      id: 'preferences',
      title: 'Preferências de Uso',
      description: 'Configure suas preferências de uso e privacidade',
      component: (
        <PreferencesStep
          data={formData}
          onChange={handleFormDataChange}
          onValidationChange={(isValid) => updateStepValidation(4, isValid)}
        />
      ),
      isValid: stepValidation[4]
    },
    {
      id: 'review',
      title: 'Revisão e Finalização',
      description: 'Confirme se todos os dados estão corretos. Você poderá editar essas informações posteriormente nas configurações do perfil.',
      component: (
        <ReviewStep
          data={formData}
          onConfirm={handleSubmit}
          isLoading={isSubmitting}
        />
      ),
      isValid: stepValidation[5]
    }
  ]

  // Verificação inicial
  useEffect(() => {
    // Se usuário já estiver logado, redirecionar
    const user = useAuthStore.getState().user
    if (user) {
      navigate('/chat')
    }
  }, [navigate])

  return (
    <div className="min-h-screen bg-brand-bg py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-brand-text mb-4">
            Criar Cadastro RegenerAI
          </h1>
          <p className="text-brand-text-secondary max-w-2xl mx-auto">
            Para que o RegenerAI entregue respostas mais adequadas à sua realidade,
            algumas informações ajudam a entender melhor seu contexto produtivo ou profissional.
          </p>
          <p className="text-brand-text-secondary text-sm mt-2">
            Você pode preencher apenas o básico no primeiro momento e completar o restante depois.
          </p>
        </div>

        {/* Erro geral */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/20 rounded-xl border border-red-200 dark:border-red-800">
            <p className="text-red-700 dark:text-red-300">{errors.general}</p>
          </div>
        )}

        {/* Wizard */}
        <QuestionnaireWizard
          steps={steps}
          currentStep={currentStep}
          onStepChange={handleStepChange}
          onComplete={() => { }} // Handle pelo ReviewStep
          isLoading={isSubmitting}
        />

        {/* Links auxiliares */}
        <div className="text-center mt-8">
          <p className="text-brand-text-secondary text-sm">
            Já tem uma conta?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-brand-primary hover:underline font-medium"
            >
              Faça login
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
